import { type FC, useCallback } from "react";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { cellRecipe, gaWeekendLabelStyle, rowStyle } from "./styles";

export interface DayPassOption {
  passLength: number;
  label: string;
}

interface Props {
  options: readonly DayPassOption[];
  selectedPassLength: number | null;
  onSelect: (passLength: number | null) => void;
  /** Accessible label for the group, sourced from CMS content. */
  ariaLabel: string;
}

/**
 * Day pass selector — toggle cards for filtering by pass duration
 * (e.g., "1 Day", "2 Day", "4 Day"). Uses the same visual style as
 * the weekend selector. Hidden when only one pass length is available.
 */
export const GADayPassSelector: FC<Props> = ({ options, selectedPassLength, onSelect, ariaLabel }) => {
  const storePageEvent = useStorePageEvent();

  const handleSelect = useCallback(
    (passLength: number | null) => {
      storePageEvent(
        PE_TYPE.CLICK,
        PE_KEYS.GA_DAY_PASS_TOGGLE_CLICK,
        JSON.stringify({ selected: passLength, previous: selectedPassLength }),
      );
      onSelect(passLength);
    },
    [onSelect, selectedPassLength, storePageEvent],
  );

  if (options.length <= 1) return null;

  return (
    <div className={rowStyle} role="group" aria-label={ariaLabel}>
      {options.map((opt) => {
        const isSelected = opt.passLength === selectedPassLength;
        return (
          <button
            key={opt.passLength}
            type="button"
            className={cellRecipe({ selection: isSelected ? "selected" : "unselected" })}
            onClick={() => handleSelect(isSelected ? null : opt.passLength)}
            aria-pressed={isSelected}
          >
            <span className={gaWeekendLabelStyle}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};
